import axios, { AxiosResponse } from 'axios';
import { api } from 'hooks/useApi';

type FixedModel = {
  projectSlug: string;
};

type GenerateDetailsResponse = {
  text: string;
};

type StartResponse = GenerateDetailsResponse | { task_id: string; status: string };

const POLL_INTERVAL_MS = 2500;
const MAX_POLL_ATTEMPTS = 120; // 5 minutes at 2.5s intervals

type StatusResponse = {
  status: string;
  result?: GenerateDetailsResponse;
  error?: string;
};

const pollForResult = async (
  statusUrl: string,
): Promise<AxiosResponse<GenerateDetailsResponse>> => {
  let attempt = 0;
  while (attempt < MAX_POLL_ATTEMPTS) {
    // eslint-disable-next-line no-await-in-loop
    await new Promise((resolve) => {
      setTimeout(resolve, POLL_INTERVAL_MS);
    });
    // eslint-disable-next-line no-await-in-loop
    const statusResponse = await api.get<StatusResponse>(statusUrl);

    if (statusResponse.data?.status === 'complete' && statusResponse.data?.result) {
      return {
        ...statusResponse,
        data: statusResponse.data.result,
      } as AxiosResponse<GenerateDetailsResponse>;
    }
    if (statusResponse.data?.status === 'failed') {
      throw new Error(statusResponse.data?.error || 'Generation failed');
    }
    attempt += 1;
  }
  throw new Error('Generation timed out');
};

const postGenerateProjectDetails = async ({
  projectSlug,
}: FixedModel): Promise<AxiosResponse<GenerateDetailsResponse>> => {
  const startResponse = await api.post<StartResponse>(`ai-interaction/project-details`, {
    project_slug: projectSlug,
  });

  if (
    startResponse.status === 200 &&
    startResponse.data &&
    'text' in startResponse.data
  ) {
    return startResponse as AxiosResponse<GenerateDetailsResponse>;
  }

  if (
    startResponse.status === 202 &&
    startResponse.data &&
    'task_id' in startResponse.data
  ) {
    return pollForResult(
      `ai-interaction/project-details/status/${startResponse.data.task_id}/`,
    );
  }

  return startResponse as AxiosResponse<GenerateDetailsResponse>;
};

const postGenerateDocumentDetails = async ({
  documentId,
}: {
  documentId: string;
}): Promise<AxiosResponse<GenerateDetailsResponse>> => {
  const startResponse = await api.post<StartResponse>(`ai-interaction/document-details`, {
    document_id: documentId,
  });

  if (
    startResponse.status === 200 &&
    startResponse.data &&
    'text' in startResponse.data
  ) {
    return startResponse as AxiosResponse<GenerateDetailsResponse>;
  }

  if (
    startResponse.status === 202 &&
    startResponse.data &&
    'task_id' in startResponse.data
  ) {
    return pollForResult(
      `ai-interaction/document-details/status/${startResponse.data.task_id}/`,
    );
  }

  return startResponse as AxiosResponse<GenerateDetailsResponse>;
};

export type DocumentRelatedContactSuggestionContact = {
  type: string;
  fullName: string;
  company?: string;
  email?: string;
  phone?: string;
  city?: string;
  state?: string;
  phonePlausibility?: string;
  sourceUrl?: string;
  confidence?: number;
  reasoning?: string;
};

/** Structured LLM payload */
export type DocumentRelatedContactSuggestionsResponse = {
  id: string;
  contacts: DocumentRelatedContactSuggestionContact[];
  notes?: string;
};

type ContactSuggestionsStatusResponse = {
  status: string;
  result?: DocumentRelatedContactSuggestionsResponse;
  error?: string;
};

const pollForContactSuggestionsResult = async (
  statusUrl: string,
): Promise<AxiosResponse<DocumentRelatedContactSuggestionsResponse>> => {
  let attempt = 0;
  while (attempt < MAX_POLL_ATTEMPTS) {
    // eslint-disable-next-line no-await-in-loop
    await new Promise((resolve) => {
      setTimeout(resolve, POLL_INTERVAL_MS);
    });
    // eslint-disable-next-line no-await-in-loop
    const statusResponse = await api.get<ContactSuggestionsStatusResponse>(statusUrl);

    if (statusResponse.data?.status === 'complete' && statusResponse.data?.result) {
      return {
        ...statusResponse,
        data: statusResponse.data.result,
      } as AxiosResponse<DocumentRelatedContactSuggestionsResponse>;
    }
    if (statusResponse.data?.status === 'failed') {
      throw new Error(statusResponse.data?.error || 'Generation failed');
    }
    attempt += 1;
  }
  throw new Error('Generation timed out');
};

const postGenerateDocumentRelatedContactSuggestions = async ({
  documentId,
  documentRelatedContactId,
}: {
  documentId: string;
  documentRelatedContactId: string;
}): Promise<AxiosResponse<DocumentRelatedContactSuggestionsResponse>> => {
  const startResponse = await api.post<
    DocumentRelatedContactSuggestionsResponse | { task_id: string; status: string }
  >(`ai-interaction/document-related-contact-suggestions`, {
    document_id: documentId,
    document_related_contact_id: documentRelatedContactId,
  });

  if (startResponse.status === 200 && startResponse.data) {
    return startResponse as AxiosResponse<DocumentRelatedContactSuggestionsResponse>;
  }

  if (
    startResponse.status === 202 &&
    startResponse.data &&
    'task_id' in startResponse.data
  ) {
    return pollForContactSuggestionsResult(
      `ai-interaction/document-related-contact-suggestions/status/${startResponse.data.task_id}/`,
    );
  }

  return startResponse as AxiosResponse<DocumentRelatedContactSuggestionsResponse>;
};

export type SessionEvent = {
  text: string[];
  author: string;
  role: string;
  timestamp: string; // ISO datetime
};

export type Session = {
  session_id: string;
  user_id: number;
  display_name: string;
  created_at: string; // ISO datetime
  updated_at: string; // ISO datetime
  events: SessionEvent[];
};

/**
 * One saved search whose raw filters changed as a result of a chat turn.
 * `query_md5` is the MD5 of the canonicalized raw ES query bodies for that
 * search - it changes whenever the underlying filters do, so it doubles as a
 * cache-busting version key.
 */
export type SavedSearchChange = {
  action: 'created' | 'updated' | 'deleted';
  adk_query_id: string;
  query_md5: string;
};

export type ScoutAgentChatResponse = {
  status: string;
  agent_text: string[];
  session: {
    session_id: string;
    user_id: number;
    display_name: string;
  };
  saved_search_changes?: SavedSearchChange[];
};

type ScoutChatEnqueueResponse = {
  status: string;
  task_id: string;
  session: ScoutAgentChatResponse['session'];
};

type ScoutChatTaskResult = {
  agent_text: string[];
  session_id: string;
  display_name: string | null;
  saved_search_changes?: SavedSearchChange[];
};

type ScoutChatStatusResponse = {
  status: string;
  result?: ScoutChatTaskResult;
  error?: string;
};

// How many consecutive poll *requests* (not task attempts) can fail outright
// - a dropped connection, a proxy hiccup - before giving up. A genuine
// "the task failed" response from our own status endpoint always stops
// immediately, regardless of this count.
const MAX_CONSECUTIVE_POLL_ERRORS = 3;

/**
 * The agent turn runs as a Celery task (it can legitimately take minutes),
 * so ai-interaction/chat/ only enqueues it and returns a task_id — poll here
 * for the actual response instead of holding one long-lived request open.
 */
const pollForScoutChatResult = async (
  statusUrl: string,
  session: ScoutAgentChatResponse['session'],
): Promise<AxiosResponse<ScoutAgentChatResponse>> => {
  let attempt = 0;
  let consecutivePollErrors = 0;
  while (attempt < MAX_POLL_ATTEMPTS) {
    // eslint-disable-next-line no-await-in-loop
    await new Promise((resolve) => {
      setTimeout(resolve, POLL_INTERVAL_MS);
    });
    attempt += 1;

    let statusResponse;
    try {
      // eslint-disable-next-line no-await-in-loop
      statusResponse = await api.get<ScoutChatStatusResponse>(statusUrl);
      consecutivePollErrors = 0;
    } catch (error) {
      const isGenuineTaskFailure =
        axios.isAxiosError(error) && error.response?.data?.status === 'failed';
      consecutivePollErrors += 1;
      if (isGenuineTaskFailure || consecutivePollErrors > MAX_CONSECUTIVE_POLL_ERRORS) {
        throw error;
      }
      // This specific poll request didn't make it there - the task itself
      // may well still be running fine. Try again next interval instead of
      // failing the whole chat over one dropped request.
      // eslint-disable-next-line no-continue
      continue;
    }

    if (statusResponse.data?.status === 'complete' && statusResponse.data?.result) {
      const { agent_text, display_name, saved_search_changes } =
        statusResponse.data.result;

      return {
        ...statusResponse,
        data: {
          status: 'completed',
          agent_text,
          session: {
            ...session,
            display_name: display_name ?? session.display_name,
          },
          saved_search_changes,
        },
      } as AxiosResponse<ScoutAgentChatResponse>;
    }
    if (statusResponse.data?.status === 'failed') {
      throw new Error(statusResponse.data?.error || 'Chat generation failed');
    }
  }
  throw new Error('Chat generation timed out');
};

const postScoutAgentChat = async ({
  message,
  sessionId,
}: {
  message: string;
  sessionId: string;
}): Promise<AxiosResponse<ScoutAgentChatResponse>> => {
  const enqueueResponse = await api.post<ScoutChatEnqueueResponse>(
    'ai-interaction/chat/',
    {
      message,
      session_id: sessionId,
    },
  );

  return pollForScoutChatResult(
    `ai-interaction/chat/status/${enqueueResponse.data.task_id}/`,
    enqueueResponse.data.session,
  );
};

/** Create a new chat with an initial message. Same endpoint as postScoutAgentChat but without session_id. */
const postScoutAgentNewChat = async ({
  message,
}: {
  message: string;
}): Promise<AxiosResponse<ScoutAgentChatResponse>> => {
  const enqueueResponse = await api.post<ScoutChatEnqueueResponse>(
    'ai-interaction/chat/',
    {
      message,
    },
  );

  return pollForScoutChatResult(
    `ai-interaction/chat/status/${enqueueResponse.data.task_id}/`,
    enqueueResponse.data.session,
  );
};

const getScoutAgentChatById = async (chatId: string): Promise<AxiosResponse<Session>> => {
  return api.get(`/ai-interaction/chat/${chatId}/`);
};

export type ScoutChatItem = {
  session_id: string;
  display_name: string;
  user_id: number;
  created_at: string;
  updated_at: string;
};

type AllScoutChatsResponse = {
  sessions: ScoutChatItem[];
};

const getAllScoutAgentChats = async (): Promise<AxiosResponse<AllScoutChatsResponse>> => {
  return api.get('/ai-interaction/chat/');
};

export type ScoutAgentFeedbackRating = 'up' | 'down';

const postScoutAgentFeedback = async ({
  sessionId,
  rating,
  eventTimestamp,
  reason,
}: {
  sessionId: string;
  rating: ScoutAgentFeedbackRating;
  eventTimestamp?: string;
  reason?: string;
}): Promise<AxiosResponse<void>> => {
  return api.post(`/ai-interaction/chat/${sessionId}/feedback/`, {
    rating,
    event_timestamp: eventTimestamp,
    reason,
  });
};

export default {
  postGenerateProjectDetails,
  postGenerateDocumentDetails,
  postGenerateDocumentRelatedContactSuggestions,
  postScoutAgentChat,
  postScoutAgentNewChat,
  getScoutAgentChatById,
  getAllScoutAgentChats,
  postScoutAgentFeedback,
};
